import styled from "@emotion/styled";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { Button, Tooltip } from "@kaltura/ds-react-components";
import { QuestionMark16Icon } from "@kaltura/ds-react-icons";
import { useId } from "react";

/**
 * TS representation of the data structure used in KMS for login options
 */
export type LoginOptionDetails = {
    /**
     * combination of adapter class and a name
     * (not used in components)
     */
    method?: string;

    /**
     * adapter class name
     * (not used in components)
     */
    class?: string;

    /**
     * slug used as "shortcut" url directly to login page using this auth-method
     * (not used in components)
     */
    authSlug?: string;

    /**
     * Auth-method name, the value submitted in the form if selected
     * (second part of `method` above)
     */
    name: string;

    /**
     * Auth-method name as it should be displayed to the end user (button text)
     */
    friendlyName: string;

    /**
     * additional text to display next to the selection button
     */
    helpText?: string;
};

export interface LoginOptionProps {
    details: LoginOptionDetails;
    onClick: (optionName: string) => void;
    shouldAddHelpTextPlaceholder: boolean;
}

const StyledLoginOption = styled("div")(({ theme }) => ({
    padding: theme.spacing(1.5, 0),
    display: "flex",
    alignItems: "center",
}));

const StyledOptionButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.kaltura.shape.roundness1,
    flexGrow: 1,
    flexShrink: 1,
}));

const StyledTooltipIconContainer = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    width: 24,
    height: 24,
    color: theme.kaltura.palette.tone1,
    backgroundColor: theme.kaltura.palette.tone4,
    marginLeft: theme.spacing(2),
    "&:hover, &:focus": {
        backgroundColor: theme.kaltura.palette.tone6,
        cursor: "pointer",
    },
}));
const StyledTooltip = styled(Tooltip)(({ theme }) => ({
    fontWeight: theme.kaltura.typography.fontWeightBold,
    fontSize: theme.kaltura.typography.fontSize,
    maxWidth: 150,
    textAlign: "center",
}));

const Placeholder = styled("div")(({ theme }) => ({
    width: 24,
    height: 24,
    marginLeft: theme.spacing(2),
}));

export function LoginOption({ details, onClick, shouldAddHelpTextPlaceholder = true }: LoginOptionProps) {
    const optionId = useId();
    return (
        <StyledLoginOption className={"kms-ds-login-selection-option"}>
            <StyledOptionButton
                className={"kms-ds-login-selection-option-button"}
                size={"large"}
                aria-label={translate(details.friendlyName)}
                aria-describedby={details.helpText && optionId}
                onClick={() => onClick(details.name)}
            >
                {HtmlReactParser(translate(details.friendlyName))}
            </StyledOptionButton>
            {details.helpText && (
                <StyledTooltip title={HtmlReactParser(translate(details.helpText))} placement={"top"}>
                    <StyledTooltipIconContainer
                        id={optionId}
                        aria-label={details.helpText}
                        className={"kms-ds-login-selection-option-tooltip-button"}
                        aria-hidden={true}
                    >
                        <QuestionMark16Icon />
                    </StyledTooltipIconContainer>
                </StyledTooltip>
            )}
            {shouldAddHelpTextPlaceholder && !details.helpText && <Placeholder />}
        </StyledLoginOption>
    );
}

export default LoginOption;
